import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { IconButton, Tooltip } from '@mui/material';
import {
    generateCreateEntity,
    generateLoadList,
    generateLoadEntity,
} from '../utils/mobx';
import services from '../services';
import { makeAutoObservable, runInAction } from 'mobx';
import {
    TextTiny,
    CheckboxInput,
    TextDefault,
    SemiCircleProgress,
    DarkArrowTooltipIcon,
    palette,
} from '@awarego-ui/components';
import { observer } from 'mobx-react';
import moment from 'moment';
import omit from 'lodash/omit';
import SelectionController from './SelectionController';
import isEqual from 'lodash/isEqual';
import debounceFn  from 'debounce-fn';
import {
    extractErrorMessage,
    isResetTrainingStatusEnabled,
} from '../utils/helpers';
import { caseAndDiacriticsInsensitiveSort } from '../components/table/table-helpers';
import NameAndSublistCell from '../components/manager/NameAndSublistCell';
import EmployeeRemindersModal from '../containers/assessment/AssessmentView/reminders/employee-modal';
import ListRemindersModal from '../containers/assessment/AssessmentView/reminders/list-modal';
import { bouncedEmailColumns } from '../containers/users/components/bouncedEmailColumns';

CheckboxInput.propTypes = {};

class AutomationEmployeesStore {
    usersList = [];
    loadingUsersList = null;
    addingUsersToList = null;
    initiallyLoaded = false;
    isDownloading = false;
    error = null;
    queryContext = null;
    selectedListsInFilter = [];
    usersTotal = 0;
    listType = null;
    // Bounced dialog: "show obsolete" toggle + obsolete count returned by the API.
    showObsolete = false;
    bouncedObsoleteCount = 0;
    resendingBounced = false;
    removingBounced = false;
    employeesToBeSentReminders = 0;
    loadingEmployeesToBeSentReminders = false;
    remindersSentRecently = [];
    detailsMenu = null;
    loadingSigninLink = false;
    lastSigninLinkUrl = null;

    constructor(automationStore, commonStore, mainStore) {
        makeAutoObservable(this);
        this.automationStore = automationStore;
        this.commonStore = commonStore;
        this.mainStore = mainStore;
        this.selection = new SelectionController(
            this.usersList,
            this,
            (x) => x.email
        );

        this.automationStore.editingAutomation &&
            this.toggleAllListsSelected(
                this.automationStore.editingAutomation.selectedUserListsIds
            );
        this.setMinScoreDebounced = debounceFn(this.setMinScore, { wait: 500 });
        this.setMaxScoreDebounced = debounceFn(this.setMaxScore, { wait: 500 });
    }

    setError(error, type) {
        switch (type) {
            default:
                this.error = error;
                break;
        }
    }

    setUserListSearchValue(value) {
        if (this.queryContext) this.queryContext.filter = value;
    }

    get userListSearchValue() {
        return (this.queryContext && this.queryContext.filter) || '';
    }

    get userListsSelectedDescription() {
        if (!this.automationStore.editingAutomation) return '';
        const selectedLists =
            this.automationStore.editingAutomation.userListsSelected.filter(
                (list) => this.selectedListsInFilter.includes(list.id)
            );

        if (selectedLists.length === 1) return selectedLists[0].name;
        if (
            this.selectedListsInFilter.length ===
            this.automationStore.editingAutomation.userListsSelected.length
        )
            return `All lists (${this.automationStore.editingAutomation.userListsSelected.length})`;

        return `${this.selectedListsInFilter.length} lists`;
    }

    setListId(value) {
        if (this.queryContext) this.queryContext.listId = value;
    }

    setMinScore(value) {
        if (this.queryContext) this.queryContext.minScore = value;
    }

    setMaxScore(value) {
        if (this.queryContext) this.queryContext.maxScore = value;
    }

    setScores(minScore, maxScore) {
        this.setMinScore(minScore);
        this.setMaxScore(maxScore);
    }

    setPagingParams(pageIndex, pageSize) {
        if (this.queryContext) {
            this.queryContext.p = pageIndex;
            this.queryContext.n = pageSize;
        }
    }

    setScoreName(value) {
        if (this.queryContext) this.queryContext.scoreName = value;
    }

    setQueryContext(context = {}) {
        this.queryContext = context;
    }

    setSortBy(sortBy) {
        if (this.queryContext && sortBy && sortBy.length > 0) {
            this.queryContext.order = `${sortBy[0].id} ${
                sortBy[0].desc ? 'desc' : 'asc'
            }`;
        }
    }

    setThreatArea(value) {
        if (this.queryContext) this.queryContext.threatArea = value;
    }

    handleMenuClose(e) {
        this.detailsMenu = null;
        if (e) e.stopPropagation();
    }

    handleMenuOpen(e, employee) {
        this.detailsMenu = { employee, anchorEl: e.currentTarget };
        e.stopPropagation();
    }

    loadSigninLink = generateLoadEntity(
        'loadSigninLink',
        this,
        'loadingSigninLink',
        async function (companyId, automationId, email) {
            return await services.Companies.automationsService(
                companyId
            ).getSigninLink(automationId, email);
        },
        'lastSigninLinkUrl',
        'url',
        undefined,
        { clearResultBeforeLoad: true }
    );

    openEmployeeLink(employee) {
        const automation = this.automationStore.editingAutomation;
        if (!automation || !employee || !employee.email) return null;
        const to = `/trainings/${automation.id}/user/${encodeURIComponent(
            employee.email
        )}`;
        return (
            <Tooltip title="Open employee in new tab" arrow placement="top">
                <IconButton
                    component={RouterLink}
                    to={to}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    aria-label="open-employee"
                    onClick={(e) => e.stopPropagation()}
                >
                    <OpenInNewIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        );
    }

    moreActionsMenu(employee) {
        if (!this.mainStore.isAdmin) return null;
        const automation = this.automationStore.editingAutomation;
        if (!automation) return null;
        const canRemindRow =
            !employee.completedDate &&
            !employee.started &&
            automation.canRemind;
        const auth = automation._rawData?.definition?.authentication;
        const canCopySignin =
            !automation.isScorm && auth !== 'no-email';
        const canDownloadCertificate = !!employee.completedDate;
        const canResetStatus = isResetTrainingStatusEnabled();
        if (
            !canRemindRow &&
            !canCopySignin &&
            !canDownloadCertificate &&
            !canResetStatus
        )
            return null;
        return (
            <DarkArrowTooltipIcon
                customFunction={(e) => this.handleMenuOpen(e, employee)}
                row={employee}
                title="More"
                icon={'more'}
            />
        );
    }

    async showRemindersModal(
        useSelection,
        type,
        item,
        itemName,
        reloadAfterSending = false
    ) {
        this.handleMenuClose();
        // there are five cases how this can be used
        // 1. remind all users
        // 2. remind one list exactly
        // 3. remind selected lists
        // 4. remind selected users
        // 5. remind one user exactly

        let userLists;
        if (
            type === 'list' &&
            (Array.isArray(item) || Number.isInteger(item))
        ) {
            userLists = Array.isArray(item) ? item : [item];
        }
        await this.loadEmployeesToBeSentReminders(
            useSelection,
            userLists ? { userLists: userLists } : {}
        );

        if (!this.loadingEmployeesToBeSentReminders) {
            if (this.employeesToBeSentReminders === 0) {
                this.commonStore.showConfirm(
                    'There are no employees to be reminded.',
                    'Close',
                    'Send reminders',
                    null,
                    false,
                    false
                );
            } else {
                if (type === 'user') {
                    this.commonStore.showConfirm(
                        <EmployeeRemindersModal
                            employeeCount={
                                useSelection
                                    ? this.employeesToBeSentReminders
                                    : 1
                            }
                            employeeName={itemName}
                        />,
                        'Send reminders',
                        'Send reminders',
                        async () => {
                            await this.sendReminders(
                                useSelection,
                                item ? { emails: [item] } : {}
                            );
                        }
                    );
                } else {
                    this.commonStore.showConfirm(
                        <ListRemindersModal
                            employeesToBeSentReminders={
                                this.employeesToBeSentReminders
                            }
                            remindersSentRecently={this.remindersSentRecently}
                            listName={itemName}
                            lists={userLists}
                        />,
                        'Send reminders',
                        'Send reminders',
                        async () => {
                            await this.sendReminders(
                                useSelection,
                                userLists ? { userLists: userLists } : {},
                                reloadAfterSending
                            );
                        }
                    );
                }
            }
        }
    }

    async sendReminders(useSelection, body, reloadAfterSending) {
        try {
            const companyId = this.mainStore.currentCompany.company_id;
            const automationId = this.automationStore.editingAutomation.id;
            let params;
            if (useSelection) {
                params = {
                    ...omit(this.queryContext, ['scoreName', 'subheading']),
                    ...this.selection.selectionContext,
                };
            }
            const result = await services.Companies.automationsService(
                companyId
            ).sendReminders(automationId, params, {
                ...body,
                reminderOption: 2,
            });
            if (result) {
                if (result.success) {
                    this.commonStore.success('Reminders sent!');
                } else {
                    this.commonStore.warn(
                        result.reason
                            ? result.reason
                            : 'Reminders were not sent!'
                    );
                }
            }
            if (result && result.success && reloadAfterSending)
                this.automationStore.loadRemindersOverview(
                    companyId,
                    automationId
                );
        } catch (e) {
            runInAction(() => {
                this.error = extractErrorMessage(e);
            });
        } finally {
            runInAction(() => {
                this.selection.clearSelection();
            });
        }
    }

    get pagingParams() {
        if (this.queryContext) {
            return {
                pageIndex: this.queryContext.p,
                pageSize: this.queryContext.n,
            };
        }
        return {};
    }

    get listId() {
        return this.queryContext && this.queryContext.listId;
    }

    get listIdHash() {
        return (
            this.queryContext &&
            this.queryContext.listId &&
            this.queryContext.listId.join('-')
        );
    }

    get minScore() {
        return this.queryContext && this.queryContext.minScore
            ? this.queryContext.minScore
            : 0;
    }

    get maxScore() {
        return this.queryContext && this.queryContext.maxScore
            ? this.queryContext.maxScore
            : 100;
    }

    get subheading() {
        return this.queryContext ? this.queryContext.subheading : '';
    }

    get scoreName() {
        return this.queryContext && (this.queryContext.scoreName || '');
    }

    get pageIndex() {
        return this.queryContext && this.queryContext.p;
    }

    get pageSize() {
        return this.queryContext && this.queryContext.n;
    }

    get order() {
        return this.queryContext && this.queryContext.order;
    }

    toggleListSelected = (listId) => {
        if (this.selectedListsInFilter.includes(listId))
            this.selectedListsInFilter.remove(listId);
        else this.selectedListsInFilter.push(listId);
        this.setListId(this.selectedListsInFilter);
    };

    toggleAllListsSelected = (array) => {
        if (
            isEqual(
                array.slice().sort((a, b) => a - b),
                this.selectedListsInFilter.slice().sort((a, b) => a - b)
            )
        ) {
            this.clearSelectedListsInFilter();
        } else {
            this.selectedListsInFilter.replace(array);
        }
        if (this.listId !== null) this.setListId(null);
    };

    setShowObsolete(value) {
        if (this.hideObsoleteBounces) return;
        this.showObsolete = value;
    }

    get hideObsoleteBounces() {
        const settings =
            (this.mainStore && this.mainStore.currentCompanyUISettings) || {};
        return settings['ui.bounced.hideObsolete'] === '1';
    }

    get selectedEmails() {
        if (this.selection.allSelected) {
            return this.usersList
                .filter((u) => !this.selection.unChecked[u.email])
                .map((u) => u.email);
        }
        return this.usersList
            .filter((u) => this.selection.checked[u.email])
            .map((u) => u.email);
    }

    get selectedBouncedCount() {
        return this.selectedEmails.length;
    }

    async resendBounced(companyId, automationId) {
        const emails = this.selectedEmails;
        if (!emails.length || this.resendingBounced) return;
        this.resendingBounced = true;
        this.error = null;
        try {
            const result = await services.Companies.automationsService(companyId)
                .bouncedEmailsService(automationId)
                .resend(emails);
            runInAction(() => {
                this.selection.clearSelection();
            });
            const failed = (result && result.errors) || [];
            if (failed.length) {
                this.commonStore.warn(
                    `Queued ${result.queued || 0}, resent ${
                        result.resent || 0
                    }. ${failed.length} could not be resent.`
                );
            } else {
                this.commonStore.success(
                    emails.length === 1
                        ? 'Email queued to resend'
                        : `${emails.length} emails queued to resend`
                );
            }
            await this.loadBouncedEmails(companyId, automationId);
        } catch (e) {
            runInAction(() => {
                this.error = extractErrorMessage(e);
            });
        } finally {
            runInAction(() => {
                this.resendingBounced = false;
            });
        }
    }

    async removeBounced(companyId, automationId) {
        const emails = this.selectedEmails;
        if (!emails.length || this.removingBounced) return;
        this.removingBounced = true;
        this.error = null;
        try {
            await services.Companies.automationsService(companyId)
                .bouncedEmailsService(automationId)
                .remove(emails);
            runInAction(() => {
                this.selection.clearSelection();
            });
            this.commonStore.success(
                emails.length === 1
                    ? 'User removed from lists'
                    : `${emails.length} users removed from lists`
            );
            await this.loadBouncedEmails(companyId, automationId);
        } catch (e) {
            runInAction(() => {
                this.error = extractErrorMessage(e);
            });
        } finally {
            runInAction(() => {
                this.removingBounced = false;
            });
        }
    }

    clearSelectedListsInFilter = () => {
        this.selectedListsInFilter.replace([]);
    };

    async downloadCertificate(companyId, automationId, email) {
        try {
            const options = { responseType: 'blob' };
            const response = await services.Companies.automationsService(
                companyId
            ).downloadCertificate(automationId, { email }, options);

            const fileName = this._extractFileName(
                response.headers['content-disposition'],
                'certificate.pdf'
            );
            this._saveBlob(
                response.data,
                response.headers['content-type'] || 'application/pdf',
                fileName
            );
        } catch (e) {
            this.commonStore && this.commonStore.warn
                ? this.commonStore.warn(
                      'Failed to download certificate. The user may not have completed the training.'
                  )
                : null;
            this.error = extractErrorMessage(e);
        }
    }

    async resetUserStatus(companyId, automationId, email) {
        try {
            const result = await services.Companies.automationsService(
                companyId
            ).resetUserStatus(automationId, email);
            if (this.commonStore && this.commonStore.success) {
                this.commonStore.success('Training status was reset.');
            }
            await this.loadEmployeesList(companyId, automationId);
            return result;
        } catch (e) {
            this.error = extractErrorMessage(e);
            if (this.commonStore && this.commonStore.warn) {
                this.commonStore.warn('Failed to reset training status.');
            }
        }
    }

    async downloadCertificatesZip(companyId, automationId, emails) {
        if (this.isDownloading) return true;
        this.isDownloading = true;
        try {
            const options = { responseType: 'blob' };
            const params = emails && emails.length > 0
                ? { emails: emails.join(',') }
                : {};
            const response = await services.Companies.automationsService(
                companyId
            ).downloadCertificatesZip(automationId, params, options);

            const fileName = this._extractFileName(
                response.headers['content-disposition'],
                'certificates.zip'
            );
            this._saveBlob(
                response.data,
                response.headers['content-type'] || 'application/zip',
                fileName
            );
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            runInAction(() => {
                this.isDownloading = false;
                this.selection.clearSelection();
            });
        }
    }

    _extractFileName(disposition, fallback) {
        if (!disposition) return fallback;
        const match =
            /filename\*=UTF-8''([^;\n]+)/i.exec(disposition) ||
            /filename="?([^";\n]+)"?/i.exec(disposition);
        if (match && match[1]) {
            try {
                return decodeURIComponent(match[1]);
            } catch (e) {
                return match[1];
            }
        }
        return fallback;
    }

    _saveBlob(data, type, fileName) {
        const url = window.URL.createObjectURL(new Blob([data], { type }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    }

    async downloadCSV(companyId, automationId) {
        if (this.isDownloading) return true;
        this.isDownloading = true;

        // setTimeout(async () => {
        try {
            const options = {
                responseType: 'blob',
                // onDownloadProgress: (progressEvent) => {
                //     const { loaded, total } = progressEvent;
                //
                //     // setDownloadInfo({
                //     //     progress: Math.floor((loaded * 100) / total),
                //     //     loaded,
                //     //     total,
                //     //     completed: false,
                //     // });
                // },
            };

            let response;

            switch (this.listType) {
                case 'bounced':
                    response = await services.Companies.automationsService(
                        companyId
                    )
                        .bouncedEmailsService(automationId)
                        .list(
                            {
                                csv: true,
                                listIds: this.queryContext.listId,
                                obsolete: this.hideObsoleteBounces
                                    ? false
                                    : this.showObsolete,
                            },
                            options
                        );
                    break;
                default:
                    response = await services.Companies.automationsService(
                        companyId
                    )
                        .usersService(automationId)
                        .list(
                            {
                                csv: true,
                                ...omit(this.queryContext, [
                                    'scoreName',
                                    'subheading',
                                ]),
                                ...this.selection.selectionContext,
                            },
                            options
                        );
                    break;
            }
            const url = window.URL.createObjectURL(
                new Blob([response.data], {
                    type: response.headers['content-type'],
                })
            );

            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'users.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();
        } finally {
            this.isDownloading = false;
            this.selection.clearSelection();
        }
        //  }, 5000);
    }

    get columns() {
        switch (this.listType) {
            case 'bounced':
                return bouncedEmailColumns(this);
            default:
                return [
                    {
                        Header: 'Name',
                        id: 'name',
                        accessor: (x) => {
                            return x.name || x.email;
                        },
                        sortType: caseAndDiacriticsInsensitiveSort,
                        Cell: observer((x) => {
                            return (
                                <CheckboxInput
                                    label={x.value}
                                    checked={
                                        this.selection
                                            .effectiveSelectionOnCurrentPage[
                                            x.row.original.email
                                        ]
                                    }
                                    noYPadding={true} //Removes Y padding and margin, making it fit better into table row
                                    onChange={(e) => {
                                        this.selection.toggleRow(
                                            x.row.original,
                                            e.target.checked
                                        );
                                    }}
                                />
                            );
                        }),
                    },
                    {
                        Header: 'Completed date',
                        accessor: 'completedDate',
                        Cell: (x) => {
                            const employee = x.row.original;
                            if (employee.completedDate) {
                                return (
                                    <span
                                        style={{
                                            color: palette.evergreen.aspargus,
                                        }}
                                    >
                                        {moment(
                                            employee.completedDate
                                        ).format('MMM DD[, ]YYYY')}
                                    </span>
                                );
                            }
                            const total = Number(employee.total) || 0;
                            const completed = Math.round(
                                Number(employee.score) || 0
                            );
                            let color;
                            if (completed === 0) {
                                color = palette.vibrant.red;
                            } else if (
                                total > 0 &&
                                completed < total * 0.9
                            ) {
                                color = palette.vibrant.orange;
                            }
                            return (
                                <span style={color ? { color } : undefined}>
                                    {completed} completed of {total}
                                </span>
                            );
                        },
                    },
                    {
                        Header: 'List',
                        accessor: 'listName',
                        // show spaces between listNames separated by a comma
                        Cell: (x) =>
                            x.value ? (
                                x.value.replace(/,(?=[^\s])/g, ', ')
                            ) : (
                                <TextDefault lighter>
                                    List unavailable
                                </TextDefault>
                            ),
                    },
                    {
                        Header: 'Email',
                        accessor: 'email',
                    },
                    {
                        Header: this.scoreName,
                        accessor: 'scorePercent',
                        Cell: (x) => (
                            <SemiCircleProgress
                                percentage={x.value}
                                width={48}
                                top={12}
                                halfCircle
                                percentageText
                                showNA={!x.row.original.started}
                            />
                        ),
                    },
                    {
                        Header: ' ',
                        id: 'open_employee',
                        Cell: (x) => this.openEmployeeLink(x.row.original),
                    },
                    ...(this.mainStore.isAdmin
                        ? [
                              {
                                  Header: ' ',
                                  id: 'more_actions',
                                  Cell: (x) =>
                                      this.moreActionsMenu(x.row.original),
                              },
                          ]
                        : []),
                ];
        }
    }

    get hiddenColumns() {
        return [];
    }

    loadEmployeesToBeSentReminders = generateLoadEntity(
        'employeesToBeSentReminders',
        this,
        'loadingEmployeesToBeSentReminders',
        async (useSelection, body = {}) => {
            const companyId = this.mainStore.currentCompany.company_id;
            const automationId = this.automationStore.editingAutomation.id;
            let params;
            if (useSelection) {
                params = {
                    ...omit(this.queryContext, ['scoreName', 'subheading']),
                    ...this.selection.selectionContext,
                };
            }
            const result = await services.Companies.automationsService(
                companyId
            ).countReminders(automationId, params, {
                ...body,
                reminderOption: 2,
            });
            runInAction(() => {
                this.remindersSentRecently.replace(result.lastReminders);
            });
            return result.remindersCount;
        },
        'employeesToBeSentReminders'
    );

    loadBouncedEmails = generateLoadList(
        'usersList',
        this,
        'loadingUsersList',
        async (companyId, automationId) => {
            const result = await services.Companies.automationsService(
                companyId
            )
                .bouncedEmailsService(automationId)
                .list({
                    listIds: this.queryContext.listId,
                    obsolete: this.hideObsoleteBounces
                        ? false
                        : this.showObsolete,
                });
            // Tolerate both the new ({ users, obsoleteCount }) and the legacy
            // (array, possibly of email strings) response shapes during
            // independent UI/API rollout.
            const rawUsers = Array.isArray(result)
                ? result
                : result.users || [];
            const mapped = rawUsers.map((x) =>
                typeof x === 'string' ? { email: x } : x
            );
            runInAction(() => {
                this.bouncedObsoleteCount = Array.isArray(result)
                    ? 0
                    : result.obsoleteCount || 0;
                this.usersTotal = mapped.length;
                this.selection.clearSelection();
            });
            return mapped;
        },
        'usersList'
    );

    loadUsers = generateLoadList(
        'usersList',
        this,
        'loadingUsersList',
        async (companyId, automationId) => {
            const result = await services.Companies.automationsService(
                companyId
            )
                .usersService(automationId)
                .list({
                    ...omit(this.queryContext, ['scoreName', 'subheading']),
                });
            runInAction(() => {
                this.usersTotal = result.count;
                this.selection.usersTotal = result.count;
            });
            return result.users;
        },
        'usersList'
    );

    addUsersToList = generateCreateEntity(
        'addUsersToList',
        this,
        'addingUsersToList',
        async (companyId, automationId, targetListId, targetListName) => {
            const params = {
                ...omit(this.queryContext, ['scoreName', 'subheading']),
                ...this.selection.selectionContext,
            };
            if (targetListId) params.targetListId = targetListId;
            if (targetListName) params.targetListName = targetListName;
            return await services.Companies.automationsService(companyId)
                .usersService(automationId)
                .addUsersToList(params);
        }
    );

    debouncedLoadUsers = debounceFn(this.loadUsers, { wait: 500 });
    debouncedLoadBouncedEmails = debounceFn(this.loadBouncedEmails, {
        wait: 400,
    });

    async loadEmployeesList(companyId, automationId) {
        try {
            switch (this.listType) {
                case 'bounced':
                    // First open loads immediately; later filter changes
                    // (lists / show-obsolete) are debounced.
                    return this.initiallyLoaded
                        ? this.debouncedLoadBouncedEmails(
                              companyId,
                              automationId
                          )
                        : this.loadBouncedEmails(companyId, automationId);
                default:
                    return this.loadUsers(companyId, automationId);
            }
        } finally {
            this.initiallyLoaded = true;
        }
    }
}

class AutomationEmployeesStoresFactory {
    employeesStores = {};

    constructor(automationStore, commonStore, mainStore) {
        makeAutoObservable(this);
        this.automationStore = automationStore;
        this.commonStore = commonStore;
        this.mainStore = mainStore;
    }

    get modalEmployeesStore() {
        if (!this.automationStore) return null;
        if (!this.employeesStores['modal'])
            this.employeesStores['modal'] = new AutomationEmployeesStore(
                this.automationStore,
                this.commonStore,
                this.mainStore
            );

        return this.employeesStores['modal'];
    }

    get employeesOverviewStore() {
        if (!this.automationStore) return null;
        if (!this.employeesStores['overview']) {
            this.employeesStores['overview'] = new AutomationEmployeesStore(
                this.automationStore,
                this.commonStore,
                this.mainStore
            );
            this.employeesStores['overview'].listType = 'users';
            this.employeesStores['overview'].queryContext = {
                n: 10,
                p: 0,
                order: 'scorePercent asc',
                filter: '',
            };
            this.employeesStores['overview'].setScoreName('Score');
        }

        return this.employeesStores['overview'];
    }

    hideEmployeesModal() {
        // just recreate instead cleanup props
        this.employeesStores['modal'] = new AutomationEmployeesStore(
            this.automationStore,
            this.commonStore,
            this.mainStore
        );
    }

    showEmployeesModal(type, context = {}) {
        this.modalEmployeesStore.listType = type;
        this.modalEmployeesStore.queryContext = context;
        this.modalEmployeesStore.showObsolete = false;
        this.modalEmployeesStore.bouncedObsoleteCount = 0;
        this.modalEmployeesStore.initiallyLoaded = false;
    }
}

export default AutomationEmployeesStoresFactory;
